/**
 * Image Config Parser Hook
 *
 * Parses and resolves image upload configuration from YAML or object format.
 * Handles tier-based overrides and validates configuration with memoization.
 *
 * Features:
 * - YAML string parsing with error handling
 * - Tier override resolution with proper precedence
 * - Configuration validation with warnings (non-blocking)
 * - Memoization to prevent unnecessary recalculations
 */

import { useMemo } from 'react'
import { AppLogger } from '@/lib/logger'
import type { Logger } from '@/lib/logger'
import { resolveImageConfig } from '@/lib/imageUploadUtils'
import type { ImageUploadConfig } from '@/lib/imageUploadTypes'

// ============================================================================
// Logger (Lazy Initialization)
// ============================================================================

let _log: Logger | null = null

function getLog(): Logger {
  if (!_log) {
    _log = AppLogger.getLogger('hooks/useImageConfigParser')
  }
  return _log
}

// ============================================================================
// Configuration Validation
// ============================================================================

/**
 * Validate resolved image upload configuration.
 * Checks for required fields and valid values, logging warnings for issues.
 * Does NOT throw - allows graceful degradation.
 *
 * @param config - Configuration to validate
 * @returns true if config passes validation (or warnings logged only)
 */
function validateImageConfig(config: ImageUploadConfig): boolean {
  const log = getLog()
  let hasErrors = false

  if (!config.allowedTypes || config.allowedTypes.length === 0) {
    log.warn('Config validation: allowedTypes is empty')
    hasErrors = true
  }

  const maxSize = config.maxFileSize
  // Check if maxSize is a positive number (if it's not unlimited string)
  const isValidSize = typeof maxSize === 'string' ? maxSize === 'unlimited' : (typeof maxSize === 'number' && maxSize > 0)
  if (!isValidSize) {
    log.warn(
      `Config validation: maxFileSize should be positive number or "unlimited", got ${maxSize}`
    )
    hasErrors = true
  }

  if (config.compression?.quality !== undefined) {
    if (typeof config.compression.quality !== 'number' || config.compression.quality < 0 || config.compression.quality > 100) {
      log.warn(
        `Config validation: compression.quality should be 0-100, got ${config.compression.quality}`
      )
      hasErrors = true
    }
  }

  if (config.dimensions?.aspectRatios) {
    const validPresets = ['1:1', '4:3', '16:9', '3:2', 'free']
    const invalidRatios = config.dimensions.aspectRatios.filter(
      (ar) => !validPresets.includes(ar)
    )
    if (invalidRatios.length > 0) {
      log.warn(
        `Config validation: Invalid aspect ratio presets: ${invalidRatios.join(', ')}. Valid: ${validPresets.join(', ')}`
      )
      hasErrors = true
    }
  }

  return !hasErrors
}

// ============================================================================
// useImageConfigParser Hook
// ============================================================================

/**
 * Hook to parse and resolve image upload configuration.
 *
 * Workflow:
 * 1. Parse YAML string to ImageUploadConfig if needed
 * 2. Resolve tier overrides using utility function
 * 3. Validate resolved config (warnings only, no exceptions)
 * 4. Memoize result to prevent recalculation
 *
 * @param rawConfig - Configuration as string (YAML) or object, or undefined
 * @param tier - Optional tier name to apply (e.g., 'free', 'pro')
 * @returns Resolved ImageUploadConfig or null if config is missing/invalid
 *
 * @example
 * const config = useImageConfigParser(uploaderYaml, 'pro')
 * if (!config) {
 *   return <p>No configuration</p>
 * }
 * return <FileUpload config={config} />
 */
export function useImageConfigParser(
  rawConfig: string | ImageUploadConfig | undefined,
  tier?: string
): ImageUploadConfig | null {
  const log = getLog()

  const resolvedConfig = useMemo(() => {
    if (!rawConfig) {
      log.warn('No config provided to useImageConfigParser')
      return null
    }

    try {
      // Resolve config with tier overrides
      const resolved = resolveImageConfig(rawConfig, tier)

      // Validate resolved config
      const isValid = validateImageConfig(resolved)
      if (!isValid) {
        log.warn('Configuration has validation warnings, but will proceed')
      }

      log.debug(`Config resolved for tier: ${tier || 'base'}`)
      return resolved
    } catch (err) {
      const message = err instanceof Error ? err.message : String(err)
      log.error(`Error parsing config: ${message}`)
      return null
    }
  }, [rawConfig, tier, log])

  return resolvedConfig
}
